import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './sections.module.css';
import CTAGroup from '../CTAGroup';
import ExpandableCard from '../ExpandableCard';
import {
  RefreshIcon,
  MapPinIcon,
  ChartIcon,
  ListIcon,
  CardIcon,
} from '../MarketingIcons';

const PROFESSIONALS = [
  {
    Icon: RefreshIcon,
    title: 'Details that stay current',
    summary: 'When they change role or number, your copy of their contact changes too.',
    body: 'You always hold the current version, not the snapshot from the day you met. Update your own profile once and everyone you have shared it with sees the change.',
  },
  {
    Icon: MapPinIcon,
    title: 'Every meeting, remembered',
    summary: 'Place, date and private notes stay with the person.',
    body: 'Write down what you talked about while it is fresh. Notes stay private to you, including on a company account. A map of the year jogs the room back into view.',
  },
  {
    Icon: ChartIcon,
    title: 'Find them when they matter',
    summary: 'Search, a map of the year, and analytics on who engaged.',
    body: 'See who opened your profile, which links they clicked and how often your card is being used, so you can tell a productive room from a busy one.',
  },
];

const TEAMS = [
  {
    Icon: ListIcon,
    title: 'Every live contact in one hub',
    summary: 'Everyone your team meets, deduplicated, with current details.',
    body: 'Not a drawer of cards, not fifty personal phones. The hub shows each person\u2019s current details rather than what was true on the day you met.',
  },
  {
    Icon: ChartIcon,
    title: 'Networking you can actually see',
    summary: 'Cards shared, contacts captured, engagement by person and by team.',
    body: 'Leaderboards so you can see who is genuinely building the network and who needs support, without reading anyone\u2019s private notes.',
  },
  {
    Icon: CardIcon,
    title: 'Cards that carry the company',
    summary: 'Customised metal cards, each tied to a live profile.',
    body: 'Our design team will work up the artwork with you, or send us your own and we will produce it. The card is how your people share in the room; the hub is what the business keeps.',
  },
];

const TEAMS_COMING = [
  {
    title: 'The account belongs to the role',
    body: 'Seats attach to a position rather than a person, so when someone leaves the organisation keeps the relationships and their successor inherits the contact base.',
    status: 'In development',
  },
  {
    title: 'Org charting, drawn for you',
    body: 'Velox builds the chart from your teams, reporting lines and seats, so you can see who owns which relationships without maintaining it by hand.',
    status: 'In development',
  },
  {
    title: 'Which events are actually worth it',
    body: 'Compare occasions and event types by what came out of them, so next year\u2019s budget goes to the ones that produced business rather than the ones that filled the calendar.',
    status: 'In development',
  },
];

const AudienceSection = () => {
  const [audience, setAudience] = useState('professionals');
  const professionalsTabId = useId();
  const teamsTabId = useId();
  const professionalsPanelId = useId();
  const teamsPanelId = useId();
  const isProfessionals = audience === 'professionals';

  return (
    <section className="vxm-section" id="who">
      <div className="vxm-container">
        <div className="vxm-section-head vxm-reveal">
          <p className="vxm-eyebrow">Who it is for</p>
          <h2 className="vxm-h2">Professionals, and the teams they work in</h2>
          <p className="vxm-body">
            The same platform either way. The fork is whether the network stays
            with you, or belongs to the business.
          </p>
        </div>

        <div
          className={`${styles.audienceToggle} vxm-reveal`}
          role="tablist"
          aria-label="Choose an audience"
        >
          <button
            type="button"
            role="tab"
            id={professionalsTabId}
            aria-selected={isProfessionals}
            aria-controls={professionalsPanelId}
            className={`${styles.audienceTab} ${isProfessionals ? styles.audienceTabActive : ''}`}
            onClick={() => setAudience('professionals')}
          >
            Professionals
          </button>
          <button
            type="button"
            role="tab"
            id={teamsTabId}
            aria-selected={!isProfessionals}
            aria-controls={teamsPanelId}
            className={`${styles.audienceTab} ${!isProfessionals ? styles.audienceTabActive : ''}`}
            onClick={() => setAudience('teams')}
          >
            Teams
          </button>
        </div>

        <div
          role="tabpanel"
          id={professionalsPanelId}
          aria-labelledby={professionalsTabId}
          hidden={!isProfessionals}
        >
          <div className={styles.cardGrid}>
            {PROFESSIONALS.map(({ Icon, title, summary, body }) => (
              <ExpandableCard
                key={title}
                title={title}
                summary={summary}
                icon={<Icon />}
                alwaysOpenOnDesktop
              >
                <p className="vxm-body">{body}</p>
              </ExpandableCard>
            ))}
          </div>
          <div className={styles.sectionFoot}>
            <CTAGroup source="professionals_section" secondary="shop" />
            <p className="vxm-body">
              <Link to="/professionals" className="vxm-link">
                More on Velox for professionals
              </Link>
            </p>
          </div>
        </div>

        <div
          role="tabpanel"
          id={teamsPanelId}
          aria-labelledby={teamsTabId}
          hidden={!isProfessionals ? undefined : true}
        >
          <div className={styles.cardGrid}>
            {TEAMS.map(({ Icon, title, summary, body }) => (
              <ExpandableCard
                key={title}
                title={title}
                summary={summary}
                icon={<Icon />}
                alwaysOpenOnDesktop
              >
                <p className="vxm-body">{body}</p>
              </ExpandableCard>
            ))}
          </div>

          <div className={styles.comingBlock}>
            <h3 className={styles.featureGroupTitle}>Coming for teams</h3>
            <div className={styles.cardGrid}>
              {TEAMS_COMING.map((item) => (
                <ExpandableCard
                  key={item.title}
                  title={item.title}
                  status={item.status}
                  alwaysOpenOnDesktop
                >
                  <p className="vxm-body">{item.body}</p>
                </ExpandableCard>
              ))}
            </div>
          </div>

          <div className={styles.sectionFoot}>
            <CTAGroup
              source="teams_section"
              intent="team"
              primaryLabel="Tell us about your team"
              showSecondary={false}
            />
            <p className="vxm-body">
              <Link to="/enterprise" className="vxm-link">
                Explore Velox for teams
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AudienceSection;
